import http from "helpers/http.js";

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Get user profile summary
 * @returns {Promise<Object>} User profile data
 */
const getProfile = () => {
    var config = {
        method: 'get',
        url: API_URL + 'api/Authenticate/get-profile',
        headers: {
            'Content-Type': 'application/json'
        }
    };

    return http(config)
        .then(function (response) {
            return response.data;
        })
        .catch(function (error) {
            console.log(error);
            return error.response?.data || { Status: "Error", Message: "Failed to get profile" };
        });
};

/**
 * Get user's redeemed vouchers
 * @returns {Promise<Array>} List of user vouchers
 */
const getUserVouchers = () => {
    var config = {
        method: 'get',
        url: API_URL + 'api/Authenticate/get-user-vouchers',
        headers: {
            'Content-Type': 'application/json'
        }
    };

    return http(config)
        .then(function (response) {
            return response.data;
        })
        .catch(function (error) {
            console.log(error);
            return error.response?.data || { Status: "Error", Message: "Failed to get vouchers" };
        });
};

/**
 * Change user password
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @param {string} confirmNewPassword - Confirm new password
 * @returns {Promise<Object>} Success or error response
 */
const changePassword = (currentPassword, newPassword, confirmNewPassword) => {
    var data = JSON.stringify({
        "CurrentPassword": currentPassword,
        "NewPassword": newPassword,
        "ConfirmNewPassword": confirmNewPassword
    });

    var config = {
        method: 'post',
        url: API_URL + 'api/Authenticate/change-password',
        headers: {
            'Content-Type': 'application/json'
        },
        data: data
    };

    return http(config)
        .then(function (response) {
            // Check if response indicates an error
            if (response.data.Status === "Error") {
                return Promise.reject(response.data);
            }
            return response.data;
        })
        .catch(function (error) {
            console.log(error);
            // If it's already a rejected promise from above, re-throw it
            if (error.Status === "Error") {
                return Promise.reject(error);
            }
            // Otherwise, it's an HTTP error - extract and reject with the error data
            const errorData = error.response?.data || { Status: "Error", Message: "Failed to change password" };
            return Promise.reject(errorData);
        });
};

/**
 * Update user email
 * @param {string} newEmail - New email address
 * @param {string} password - Current password for confirmation
 * @returns {Promise<Object>} Success response with new token or error
 */
const updateEmail = (newEmail, password) => {
    var data = JSON.stringify({
        "NewEmail": newEmail,
        "Password": password
    });

    var config = {
        method: 'post',
        url: API_URL + 'api/Authenticate/update-email',
        headers: {
            'Content-Type': 'application/json'
        },
        data: data
    };

    return http(config)
        .then(function (response) {
            // Check if response indicates an error
            if (response.data.Status === "Error") {
                return Promise.reject(response.data);
            }
            // If email update successful, update stored user data with new token
            if (response.data.token) {
                const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
                const updatedUser = {
                    ...currentUser,
                    token: response.data.token,
                    email: response.data.email,
                    userName: response.data.userName,
                    expiration: response.data.expiration
                };
                localStorage.setItem("user", JSON.stringify(updatedUser));
            }
            return response.data;
        })
        .catch(function (error) {
            console.log(error);
            // If it's already a rejected promise from above, re-throw it
            if (error.Status === "Error") {
                return Promise.reject(error);
            }
            // Otherwise, it's an HTTP error - extract and reject with the error data
            const errorData = error.response?.data || { Status: "Error", Message: "Failed to update email" };
            return Promise.reject(errorData);
        });
};

/**
 * Get user's purchase history from Stripe
 * @returns {Promise<Object>} Purchase history with items grouped by payment
 */
const getPurchaseHistory = () => {
    var config = {
        method: 'get',
        url: API_URL + 'api/Authenticate/get-purchase-history',
        headers: {
            'Content-Type': 'application/json'
        }
    };

    return http(config)
        .then(function (response) {
            if (response.data.Status === "Error") {
                return Promise.reject(response.data);
            }
            return response.data;
        })
        .catch(function (error) {
            console.log(error);
            if (error.Status === "Error") {
                return Promise.reject(error);
            }
            const errorData = error.response?.data || { Status: "Error", Message: "Failed to load purchase history" };
            return Promise.reject(errorData);
        });
};

export default {
    getProfile,
    getUserVouchers,
    changePassword,
    updateEmail,
    getPurchaseHistory
};
